<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- 1. الترويسة -->
    <div class="d-flex justify-content-between align-items-center mb-4 no-print">
      <div>
        <h4 class="fw-bold text-success">
          <i class="fas fa-check-double ms-2"></i> ملفات التظلمات الجاهزة
        </h4>
        <p class="text-muted small mb-0">قائمة الطلاب الذين تم تجهيز أوراقهم ومتاح لهم الحضور للاطلاع</p>
      </div>
      
      <div class="d-flex gap-2">
        <button @click="printPage" class="btn btn-outline-dark shadow-sm">
          <i class="fas fa-print ms-1"></i> طباعة الكشف
        </button>
      </div>
    </div>

    <!-- 2. البحث -->
    <div class="card shadow-sm mb-4 border-top-success no-print">
      <div class="card-body">
        <div class="input-group">
          <span class="input-group-text bg-white"><i class="fas fa-search text-success"></i></span>
          <input 
            v-model="search" 
            @input="debouncedFetch" 
            type="text" 
            class="form-control" 
            placeholder="بحث برقم الجلوس أو الاسم..." 
          />
        </div>
      </div>
    </div>

    <!-- 3. الجدول -->
    <div class="card shadow-sm" id="printSection">
      <div class="card-header bg-white d-print-none d-flex justify-content-between align-items-center">
        <span class="fw-bold text-success">عدد الملفات الجاهزة: {{ pagination.total }}</span>
        <span v-if="loading" class="spinner-border spinner-border-sm text-success"></span>
      </div>
      
      <div class="table-responsive">
        <table class="table table-hover align-middle text-center mb-0">
          <thead class="table-success">
            <tr>
              <th>رقم الجلوس</th>
              <th style="width: 25%">اسم الطالب</th>
              <th style="width: 35%">مراجعة الصور (المواد)</th>
              <th>تاريخ الاطلاع</th>
              <th class="no-print">موظف التجهيز</th>
              <th class="no-print">الإجراء</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="req in appeals" :key="req.id">
              <!-- رقم الجلوس -->
              <td class="fw-bold fs-5">{{ req.Seating_No }}</td>
              
              <!-- اسم الطالب -->
              <td class="text-start fw-bold">{{ req.student_name }}</td>
              
              <!-- المواد (أزرار للذهاب للمراجعة) -->
              <td>
                <div class="d-flex flex-wrap justify-content-center gap-2">
                  <button 
                    v-for="sub in req.subjects" 
                    :key="sub.id" 
                    @click="goToImageReview(sub.id)"
                    class="btn btn-sm d-flex align-items-center gap-1 shadow-sm"
                    :class="sub.paper_status === 'ready' ? 'btn-success' : 'btn-outline-secondary'"
                    title="اضغط لمراجعة صور الورقة وحذف الزيادات"
                  >
                    <i class="fas fa-images"></i>
                    {{ sub.subject_name }}
                  </button>
                </div>
              </td>

              <!-- التوقيت -->
              <td dir="ltr" class="text-muted small">
                {{ req.visit_date }}
              </td>

              <!-- الموظف -->
              <td class="no-print">
                <span class="badge bg-light text-secondary border">
                  <i class="fas fa-user ms-1"></i> {{ req.preparer?.name || '---' }}
                </span>
              </td>

              <!-- الأكشن (تسجيل النتيجة) -->
              <td class="no-print">
                <button 
                  @click="openReviewModal(req)" 
                  class="btn btn-outline-dark btn-sm shadow-sm"
                  title="تسجيل ملاحظات الطالب بعد الاطلاع"
                >
                  <i class="fas fa-edit ms-1"></i> النتائج
                </button>
              </td>
            </tr>
            
            <tr v-if="appeals.length === 0 && !loading">
              <td colspan="6" class="py-5 text-muted">
                <i class="fas fa-folder-open fa-2x mb-3 d-block opacity-50"></i>
                لا توجد ملفات جاهزة حالياً.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- 4. التصفح (Pagination) -->
      <div class="card-footer bg-white d-flex justify-content-between align-items-center py-3 no-print" v-if="pagination.last_page > 1">
          <div class="text-muted small">
              عرض {{ pagination.from }} إلى {{ pagination.to }} من إجمالي {{ pagination.total }} ملف
          </div>
          <nav>
              <ul class="pagination pagination-sm mb-0">
                  <!-- زر السابق -->
                  <li class="page-item" :class="{ disabled: !pagination.prev_page_url }">
                      <button class="page-link" @click="changePage(pagination.current_page - 1)">
                          <i class="fas fa-chevron-right"></i> السابق
                      </button>
                  </li>

                  <!-- أرقام الصفحات -->
                  <li class="page-item" :class="{ active: page === pagination.current_page }" 
                      v-for="page in getPageNumbers" :key="page">
                      <button class="page-link" @click="changePage(page)">{{ page }}</button>
                  </li>

                  <!-- زر التالي -->
                  <li class="page-item" :class="{ disabled: !pagination.next_page_url }">
                      <button class="page-link" @click="changePage(pagination.current_page + 1)">
                          التالي <i class="fas fa-chevron-left"></i>
                      </button>
                  </li>
              </ul>
          </nav>
      </div>

    </div>

    <!-- ============================================= -->
    <!-- 5. نافذة تسجيل الملاحظات (Review Modal) -->
    <!-- ============================================= -->
    <div v-if="showModal" class="modal fade show d-block" style="background: rgba(0,0,0,0.5); z-index: 1050;">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title fw-bold"><i class="fas fa-file-signature ms-2"></i>تسجيل نتيجة الاطلاع</h5>
            <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
          </div>
          
          <div class="modal-body" v-if="selectedAppeal">
            <div class="alert alert-info border-info d-flex align-items-center mb-4">
              <i class="fas fa-info-circle fa-2x me-3"></i>
              <div>
                <strong>تعليمات هامة:</strong> قم بوضع علامة (صح) أمام المواد التي كتب الطالب فيها ملاحظات <u>فقط</u>.
                <br>سيقوم النظام تلقائياً بتوليد <strong>رقم سري جديد</strong> لهذه المواد لإعادة تصحيحها.
              </div>
            </div>

            <h5 class="fw-bold mb-3 border-bottom pb-2 d-flex justify-content-between">
              <span>الطالب: {{ selectedAppeal.student_name }}</span>
              <span class="badge bg-secondary fs-6">جلوس: {{ selectedAppeal.Seating_No }}</span>
            </h5>

            <div class="table-responsive border rounded">
              <table class="table table-striped align-middle text-center mb-0">
                <thead class="bg-light">
                  <tr>
                    <th style="width: 20%">له ملاحظات؟</th>
                    <th>المادة</th>
                    <th>الدرجة الحالية</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sub in selectedAppeal.subjects" :key="sub.id" 
                      :class="{'table-warning fw-bold': sub.has_remarks}">
                    <td>
                      <!-- Checkbox لتحديد المواد ذات الملاحظات -->
                      <div class="form-check d-flex justify-content-center">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          style="transform: scale(1.4); cursor: pointer;"
                          v-model="sub.has_remarks"
                          :id="'sub-'+sub.id"
                        >
                      </div>
                    </td>
                    <td class="text-start ps-4">
                        <label :for="'sub-'+sub.id" style="cursor: pointer; width: 100%;">
                            {{ sub.subject_name }}
                        </label>
                    </td>
                    <td>{{ sub.old_degree }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="modal-footer bg-light">
            <button type="button" class="btn btn-secondary px-4" @click="closeModal">إلغاء</button>
            <button 
              type="button" 
              class="btn btn-success fw-bold px-4" 
              @click="submitReview"
              :disabled="processing"
            >
              <span v-if="processing" class="spinner-border spinner-border-sm ms-1"></span>
              <i class="fas fa-save ms-1" v-else></i>
              حفظ وإنهاء الطلب
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import _ from 'lodash';
import api from '@/api/axios';

export default {
  name: "ReadyAppealsList",
  data() {
    return {
      loading: false,
      search: "",
      appeals: [],
      
      // كائن التصفح
      pagination: {
          current_page: 1,
          last_page: 1,
          total: 0,
          from: 0,
          to: 0,
          prev_page_url: null,
          next_page_url: null
      },
      page: 1,

      // متغيرات المودال
      showModal: false,
      selectedAppeal: null,
      processing: false
    };
  },
  
  computed: {
    // خوارزمية عرض أرقام الصفحات
    getPageNumbers() {
        if (!this.pagination.last_page) return [];
        let current = this.pagination.current_page;
        let last = this.pagination.last_page;
        let delta = 2;
        let left = current - delta;
        let right = current + delta + 1;
        let range = [];

        for (let i = 1; i <= last; i++) {
            if (i == 1 || i == last || (i >= left && i < right)) {
                range.push(i);
            }
        }
        return range;
    }
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    // جلب البيانات من السيرفر
    async fetchData() {
      this.loading = true;
      try {
        const res = await api.get('/appeals/ready-list', {
            params: { 
                search: this.search,
                page: this.page 
            }
        });
        
        // تعيين البيانات
        this.appeals = res.data.data;
        this.pagination = res.data; // الميتا داتا الكاملة
        
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    // تغيير الصفحة
    changePage(pageNumber) {
        if (pageNumber < 1 || pageNumber > this.pagination.last_page) return;
        this.page = pageNumber;
        this.fetchData();
    },

    // بحث سريع
    debouncedFetch: _.debounce(function() {
      this.page = 1; // العودة للصفحة الأولى
      this.fetchData();
    }, 500),

    // الذهاب لصفحة مراجعة الصور
    goToImageReview(subjectId) {
        this.$router.push({
            name: 'PaperQualityReview',
            params: { subjectId: subjectId }
        });
    },

    // فتح المودال
    openReviewModal(req) {
      this.selectedAppeal = JSON.parse(JSON.stringify(req));
      
      // إضافة خاصية التحديد
      if (this.selectedAppeal.subjects) {
          this.selectedAppeal.subjects.forEach(sub => {
              this.$set(sub, 'has_remarks', false); 
          });
      }
      
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedAppeal = null;
    },

    // إرسال النتيجة النهائية
    async submitReview() {
      if (!this.selectedAppeal) return;

      const remarksSubjects = this.selectedAppeal.subjects
          .filter(s => s.has_remarks)
          .map(s => s.id);

      if (remarksSubjects.length === 0) {
          if(!confirm("لم يتم اختيار أي مادة للمراجعة. هل أنت متأكد أن الطالب ليس لديه ملاحظات؟")) return;
      }

      this.processing = true;
      
      try {
        await api.post(`/appeals/${this.selectedAppeal.id}/submit-review`, {
            subject_ids: remarksSubjects
        });

        alert("تم حفظ النتيجة وتوليد الأرقام السرية بنجاح.");
        
        this.closeModal();
        this.fetchData(); // تحديث القائمة
        
      } catch (error) {
        console.error(error);
        alert("حدث خطأ أثناء الحفظ، حاول مرة أخرى.");
      } finally {
        this.processing = false;
      }
    },

    printPage() {
        window.print();
    }
  }
};
</script>

<style scoped>
.border-top-success { border-top: 4px solid #198754; }

/* تنسيق أزرار التصفح */
.page-link {
    cursor: pointer;
    color: #198754;
}
.page-item.active .page-link {
    background-color: #198754;
    border-color: #198754;
    color: white;
}
.page-item.disabled .page-link {
    color: #6c757d;
    pointer-events: none;
    background-color: #fff;
    border-color: #dee2e6;
}

/* تنسيق الطباعة */
@media print {
    .no-print { display: none !important; }
    .card { border: none !important; shadow: none !important; }
    
    table { width: 100% !important; font-size: 12px; }
    th, td { border: 1px solid #000 !important; }
    
    #printSection::before {
        content: "كشف تسليم ملفات التظلمات الجاهزة";
        display: block;
        text-align: center;
        font-size: 20px;
        font-weight: bold;
        margin-bottom: 20px;
    }
    
    .modal { display: none !important; }
}
</style>